import React, { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import {
  getBubbleContainerStyles,
  getBubbleContentStyles,
  getBubbleStyles,
  getActionButtonContainerStyles,
} from '../styles/bubbleStyles';
import { MessageActions, type MessageAction } from './MessageActions';
import { highlightSearchTerm } from '../utils/markdown/highlightSearch';

export interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;

  // Avatar
  showAvatar?: boolean;
  avatarElement?: React.ReactNode;

  // Actions
  actions?: MessageAction[];

  // Editing (for user messages)
  onEdit?: (newContent: string) => void;
  isEditing?: boolean;
  onStartEdit?: () => void;
  onCancelEdit?: () => void;

  // Markdown config
  markdownComponents?: object;
  searchTerm?: string;

  // Styling
  className?: string;

  // Additional element to render alongside actions (e.g., inline sources)
  actionsAddon?: React.ReactNode;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  role,
  content,
  showAvatar = false,
  avatarElement,
  actions = [],
  onEdit,
  isEditing = false,
  onCancelEdit,
  markdownComponents,
  searchTerm,
  className = '',
  actionsAddon,
}) => {
  const [editedContent, setEditedContent] = useState(content);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea when editing
  const handleEditChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedContent(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  const handleEditSubmit = () => {
    if (editedContent.trim() && onEdit) {
      onEdit(editedContent);
      setEditedContent(content);
    }
  };

  const handleEditCancel = () => {
    setEditedContent(content);
    onCancelEdit?.();
  };

  // Auto-resize when entering edit mode
  React.useEffect(() => {
    if (isEditing && editTextareaRef.current) {
      setTimeout(() => {
        if (editTextareaRef.current) {
          editTextareaRef.current.style.height = 'auto';
          editTextareaRef.current.style.height = `${editTextareaRef.current.scrollHeight}px`;
        }
      }, 0);
    }
  }, [isEditing]);

  return (
    <div className={cn(getBubbleContainerStyles(role), className)}>
      {/* Avatar (only for user messages when enabled) */}
      {role === 'user' && showAvatar && avatarElement && (
        <div className="flex flex-col items-center">{avatarElement}</div>
      )}

      <div className={cn(getBubbleContentStyles(role), 'relative')}>
        <div
          className={cn(
            getBubbleStyles(role),
            role === 'user' && isEditing && 'min-w-[300px] pb-10',
          )}
        >
          <div className="break-words">
            {role === 'user' && isEditing && (
              <Textarea
                ref={editTextareaRef}
                value={editedContent}
                onChange={handleEditChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleEditSubmit();
                  }
                  if (e.key === 'Escape') {
                    handleEditCancel();
                  }
                }}
                className="w-full bg-transparent text-primary-foreground border-0 resize-none p-0 m-0 min-h-0 h-auto focus-visible:ring-0 focus-visible:ring-offset-0 outline-none shadow-none overflow-hidden"
                style={{
                  lineHeight: '1.5',
                  fontSize: '0.875rem',
                }}
                autoFocus
              />
            )}
            {role === 'user' && !isEditing && (
              <p className="break-all whitespace-pre-wrap m-0">
                {searchTerm
                  ? highlightSearchTerm(content, searchTerm)
                  : content}
              </p>
            )}
            {role === 'assistant' && (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {content}
              </ReactMarkdown>
            )}
          </div>

          {/* Edit controls (Cancel + Send buttons) */}
          {role === 'user' && isEditing && (
            <div className="absolute bottom-2 right-2 flex items-center justify-end gap-1.5 mt-2">
              <button
                type="button"
                onClick={handleEditCancel}
                className="px-2.5 py-1 rounded-full transition-all bg-primary-foreground/60 text-primary hover:bg-primary-foreground/80 text-xs font-medium"
                title="Cancel editing"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleEditSubmit}
                disabled={!editedContent.trim()}
                className={cn(
                  'px-2.5 py-1 rounded-full transition-all text-xs font-medium',
                  editedContent.trim()
                    ? 'bg-primary-foreground text-primary hover:bg-primary-foreground/90'
                    : 'bg-primary-foreground/30 text-primary/50 cursor-not-allowed',
                )}
                title="Send edited message"
              >
                Send
              </button>
            </div>
          )}
        </div>

        {/* Action buttons below the bubble */}
        {(actions.length > 0 || actionsAddon) && !isEditing && (
          <div className={cn(getActionButtonContainerStyles(role))}>
            <div className="flex items-center gap-1">
              {actions.length > 0 && <MessageActions actions={actions} />}
              {/* Inline trigger button for addon */}
              {actionsAddon}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
